import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardList, Loader2, Check, ThumbsUp } from 'lucide-react';
import { api } from '../lib/api';
import StarRating from '../components/StarRating';

export default function PublicTeacherFeedback() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [ratings, setRatings] = useState({}); // teacher_id -> {rating, comment}
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getPublicFeedbackForm(slug);
        setForm(res.data);
      } catch (e) {
        setForm({ __error: e?.response?.data?.detail || 'This feedback form is not available.' });
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const setRating = (tid, rating) => setRatings((r) => ({ ...r, [tid]: { ...(r[tid] || {}), rating } }));
  const setComment = (tid, comment) => setRatings((r) => ({ ...r, [tid]: { ...(r[tid] || {}), comment } }));

  const submit = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) { toast.error('Enter a valid 10-digit phone'); return; }

    // Every teacher must be rated
    const missing = (form?.teachers || []).filter((t) => !ratings[t.id]?.rating);
    if (missing.length > 0) {
      toast.error(`Please rate all teachers (${missing.length} pending)`);
      return;
    }

    const payload = {
      name: name.trim(),
      phone: cleanPhone,
      ratings: (form?.teachers || []).map((t) => ({
        teacher_id: t.id,
        rating: ratings[t.id].rating,
        comment: (ratings[t.id].comment || '').trim(),
      })),
    };
    setSubmitting(true);
    try {
      await api.submitPublicFeedback(slug, payload);
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300"><Loader2 className="animate-spin" /></div>;
  }
  if (form?.__error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 p-4">
        <div className="max-w-md text-center">
          <ClipboardList size={48} className="mx-auto mb-3 text-slate-500" />
          <h1 className="text-xl font-semibold text-white">Feedback unavailable</h1>
          <p className="text-sm text-slate-400 mt-2">{form.__error}</p>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-900 via-slate-950 to-emerald-900 p-4">
        <div className="max-w-md text-center bg-slate-900/70 border border-slate-700 rounded-2xl p-8 backdrop-blur">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mb-4">
            <ThumbsUp className="text-emerald-300" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white">Thanks for your feedback!</h1>
          <p className="text-sm text-slate-300 mt-2">Your ratings have been recorded and will help us improve the batch.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950/40 to-slate-950 text-slate-100 p-4 sm:p-8" data-testid="public-feedback">
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-900/70 border border-slate-700 rounded-2xl p-5 sm:p-6 backdrop-blur">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center shrink-0">
              <ClipboardList className="text-amber-300" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold">{form.title || 'Teacher Feedback'}</h1>
              <p className="text-xs text-slate-400 truncate">{form.course_name}</p>
            </div>
          </div>
          {form.description && <p className="text-sm text-slate-300 mb-5">{form.description}</p>}

          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Your name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Rohit Kumar"
                       className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                       data-testid="feedback-name" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Mobile number</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="10-digit mobile" inputMode="numeric"
                       className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                       data-testid="feedback-phone" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {(form.teachers || []).map((t) => {
              const r = ratings[t.id]?.rating || 0;
              return (
                <div key={t.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid={`feedback-teacher-${t.id}`}>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300 font-bold shrink-0">
                        {(t.name || '?')[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{t.name}</div>
                        <div className="text-[11px] text-slate-400">Rate this teacher</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating value={r} onChange={(n) => setRating(t.id, n)} size={26}
                                  testIdPrefix={`feedback-star-${t.id}`} />
                    </div>
                  </div>
                  <textarea rows={2} placeholder="Optional: leave a comment (max 1000 chars)"
                            value={ratings[t.id]?.comment || ''}
                            onChange={(e) => setComment(t.id, e.target.value.slice(0, 1000))}
                            className="w-full mt-2 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                            data-testid={`feedback-comment-${t.id}`} />
                </div>
              );
            })}
          </div>

          <button onClick={submit} disabled={submitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-500 rounded-lg font-medium disabled:opacity-60"
                  data-testid="feedback-submit-btn">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
            Submit Feedback
          </button>
          <p className="text-[11px] text-slate-500 text-center mt-3">Same phone number can submit again after 7 days.</p>
        </div>
      </div>
    </div>
  );
}
